import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  ChevronLeft,
  ChevronRight,
  Truck,
  CheckCircle2,
  Clock,
  Users,
  CalendarDays,
  Bell,
  Wand2,
  RotateCcw,
  List,
  Map,
  CloudSnow,
  X as XIcon,
} from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import DateNavigator from '../../components/DateNavigator';
import { useDateUrl } from '../../hooks/useDateUrl';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import { useRole } from '../../context/RoleContext';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../../context/CompanyContext';
import { useCompanyTechnicians } from '../../hooks/useCompanyTechnicians';
import { isClosedAccount } from '../../utils/accountLifecycle';
import { isBoardVisibleTask } from '../../utils/buildForecast';
import DispatchTechColumn from './DispatchTechColumn';
import DispatchUnassignedRail from './DispatchUnassignedRail';
import DispatchJobCard from './DispatchJobCard';
import SendRemindersModal from './SendRemindersModal';
import DispatchMobileSummary from './DispatchMobileSummary';
import DispatchScrollSync from './DispatchScrollSync';
import BuildDaySheet from '../../components/dispatch/BuildDaySheet';
import ResetSheet from '../../components/dispatch/ResetSheet';
import MapView from '../../components/dispatch/MapView';
import BulkRescheduleModal from '../../components/scheduler/BulkRescheduleModal';
import ReminderBatchPanel from '../../components/dispatch/ReminderBatchPanel';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ────────────────────────────────────────────────────────────────────

function isSameDay(dateStr, referenceDate) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === referenceDate.getFullYear() &&
    d.getMonth()    === referenceDate.getMonth() &&
    d.getDate()     === referenceDate.getDate()
  );
}

function formatDateHeader(date) {
  const today = new Date();
  const isToday =
    date.getDate()     === today.getDate() &&
    date.getMonth()    === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const label = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });

  return isToday ? `Today — ${label}` : label;
}

function byDispatchOrder(a, b) {
  // Two-mode sort tiers:
  //   1. Both have dispatchOrder  → numeric order (Build Day output).
  //   2. One has dispatchOrder    → that one first.
  //   3. Neither has dispatchOrder:
  //        a. Appointment-mode tasks sort by scheduledStart (real customer time).
  //        b. Route-flexible tasks DO NOT sort by scheduledStart — their stored
  //           time is a synthetic placeholder. Sort by createdAt instead so
  //           they land in CSR-entry order under the "Pending Build Day" tier.
  //        c. Appointment-vs-route ties: appointments first.
  if (a.dispatchOrder != null && b.dispatchOrder != null) {
    return a.dispatchOrder - b.dispatchOrder;
  }
  if (a.dispatchOrder != null) return -1;
  if (b.dispatchOrder != null) return 1;

  const aAppt = (a.jobMode || '') === 'appointment';
  const bAppt = (b.jobMode || '') === 'appointment';
  if (aAppt && !bAppt) return -1;
  if (!aAppt && bAppt) return 1;

  if (aAppt && bAppt) {
    return String(a.scheduledStart || '').localeCompare(String(b.scheduledStart || ''));
  }
  // Both route-flexible — use createdAt (fallback to id) instead of synthetic scheduledStart.
  const aKey = String(a.createdAt || a.id || '');
  const bKey = String(b.createdAt || b.id || '');
  return aKey.localeCompare(bKey);
}

/**
 * Returns true if this task is route-flexible AND has not yet been planned
 * by Build Day. Such tasks render under a "Pending Build Day" group label
 * rather than interleaved with appointments — see §"How route jobs stay
 * visible to office staff" in /app/memory/PRD.md.
 */
export function isPendingBuildDay(task) {
  return (
    (task?.jobMode || 'route_flexible') !== 'appointment'
    && task?.dispatchOrder == null
  );
}

function fmtTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d)) return '';
  const h    = d.getHours() % 12 || 12;
  const m    = d.getMinutes();
  const ampm = d.getHours() < 12 ? 'AM' : 'PM';
  return m === 0 ? `${h} ${ampm}` : `${h}:${String(m).padStart(2, '0')} ${ampm}`;
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function DispatchPage() {
  const { tasks, updateTask, ensureTaskWindow } = useTasks();
  const { leads, ensureLeadIds } = useLeads();

  // Backfill leads referenced by visible tasks but outside the leads window
  useEffect(() => {
    const known = new Set(leads.map((l) => l.id));
    const missing = [...new Set(tasks.map((t) => t.leadId).filter(Boolean))].filter((id) => !known.has(id));
    if (missing.length) ensureLeadIds(missing);
  }, [tasks, leads, ensureLeadIds]);
  const { role, isTechnician } = useRole();
  const { user } = useAuth();
  const { companyId } = useCompany();

  // URL deep-link: ?date=YYYY-MM-DD seeds the initial dispatch date and
  // stays in sync as the operator navigates (DateNavigator pick, Today,
  // prev/next chevrons). Browser Back/Forward walks the date history.
  const { initialDate: urlDate, urlDateYmd, syncDate } = useDateUrl();
  const [dispatchDate, setDispatchDate] = useState(() => urlDate || new Date());

  // Expand the bounded task window when dispatching outside the default range
  useEffect(() => {
    const start = new Date(dispatchDate); start.setDate(start.getDate() - 7);
    const end = new Date(dispatchDate); end.setDate(end.getDate() + 7);
    ensureTaskWindow(start, end);
  }, [dispatchDate, ensureTaskWindow]);
  // Pull: react to Back/Forward by syncing state from URL.
  useEffect(() => {
    if (!urlDateYmd) return;
    const y = dispatchDate.getFullYear();
    const m = String(dispatchDate.getMonth() + 1).padStart(2, '0');
    const d = String(dispatchDate.getDate()).padStart(2, '0');
    if (`${y}-${m}-${d}` === urlDateYmd) return;
    const [yy, mm, dd] = urlDateYmd.split('-').map(Number);
    setDispatchDate(new Date(yy, mm - 1, dd, 0, 0, 0, 0));
  }, [urlDateYmd]);
  // Push: react to user-driven state changes.
  useEffect(() => { syncDate(dispatchDate); }, [dispatchDate, syncDate]);
  const [activeJobId, setActiveJobId]           = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [showPreview, setShowPreview]           = useState(false);
  const [reminderBatchId, setReminderBatchId]   = useState(null);
  const [previewData, setPreviewData]           = useState(null);
  const [previewLoading, setPreviewLoading]     = useState(false);
  const [isMobile, setIsMobile]                 = useState(() => window.innerWidth < 1024);
  // View toggle: 'list' (DnD board) or 'map' (Google Maps Route Map View).
  // Persists across the session via localStorage.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = localStorage.getItem('dispatch.viewMode');
      return v === 'map' ? 'map' : 'list';
    } catch { return 'list'; }
  });
  // Account Lifecycle (Phase A): closed accounts are hidden by default so
  // operators don't accidentally service ghost work. Toggle persists across
  // the session via localStorage.
  const [showClosed, setShowClosed] = useState(() => {
    try { return localStorage.getItem('dispatch.showClosed') === '1'; } catch { return false; }
  });
  const [buildDayOpen, setBuildDayOpen]         = useState(false);
  // Reset sheet — shared between "Reset Day" (header) and per-tech "Reset route"
  // The shape is { scope: 'day' | 'technician', techUid, techName }.
  const [resetSheet, setResetSheet]             = useState(null);

  // Sprint 2 — bulk reschedule selection + modal.
  //   selectedJobIds: live set of task ids the operator has check-marked.
  //   bulkRescheduleMode: 'day' | 'selected' | null — drives modal & label.
  const [selectedJobIds, setSelectedJobIds]   = useState(() => new Set());
  const [bulkRescheduleMode, setBulkRescheduleMode] = useState(null);

  const isAdmin = ['owner', 'admin'].includes((role || '').toLowerCase());

  const isAdminView = !isTechnician; // owner / admin / sales can manage
  const canDrag     = isAdminView;
  const currentUserUid = user?.uid;

  // Board scroll container ref — shared with the DispatchScrollSync proxy
  // above the board so both scrollbars stay in sync.
  const boardScrollRef = useRef(null);

  // DnD sensors — use pointer sensor with a small activation distance to avoid
  // accidental drags on scroll/tap
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  // ── Build leads map for quick lookup ────────────────────────────────────────
  const leadsMap = useMemo(() => {
    const map = {};
    leads.forEach((l) => { map[l.id] = l; });
    return map;
  }, [leads]);

  // ── Filter today's job tasks ─────────────────────────────────────────────────
  const allTodayJobs = useMemo(() => {
    // Hide operator/cascade-killed tasks (cancelled / no-show) from the
    // board. Completed visits remain visible as historical context.
    return tasks.filter(
      (t) => t.type === 'job'
        && isSameDay(t.scheduledStart, dispatchDate)
        && isBoardVisibleTask(t),
    );
  }, [tasks, dispatchDate]);

  // Closed-account jobs are hidden by default. Surface a count so operators
  // know they exist and can reveal them via the toggle.
  const hiddenClosedJobCount = useMemo(() => {
    if (showClosed) return 0;
    return allTodayJobs.filter((t) => isClosedAccount(leadsMap[t.leadId])).length;
  }, [allTodayJobs, leadsMap, showClosed]);

  const todayJobs = useMemo(() => {
    if (showClosed) return allTodayJobs;
    return allTodayJobs.filter((t) => !isClosedAccount(leadsMap[t.leadId]));
  }, [allTodayJobs, leadsMap, showClosed]);

  // ── Resolve technicians from company membership (canonical) + any assignees
  //    from today's jobs (covers demo seeds or historical assignees who may no
  //    longer appear in the membership subcollection). Merge union so:
  //      - any active technician from the company shows a column even with 0 jobs
  //      - any uid currently assigned to a job still gets a column, even if
  //        their membership doc is gone (prevents orphaned jobs from disappearing)
  const { technicians: companyTechs } = useCompanyTechnicians(companyId);

  const techMap = useMemo(() => {
    const map = {}; // uid → name
    // 1) Canonical source: every technician on the company membership
    companyTechs.forEach((t) => {
      map[t.uid] = t.name;
    });
    // 2) Safety net: any assignee present on today's jobs that we haven't
    //    already surfaced (covers demo seeds + edge cases).
    todayJobs.forEach((t) => {
      if (t.assignedTo && !map[t.assignedTo]) {
        map[t.assignedTo] = t.assignedToName || t.assignedTo;
      }
    });
    return map;
  }, [companyTechs, todayJobs]);

  // ── Group jobs ───────────────────────────────────────────────────────────────
  const unassignedJobs = useMemo(
    () => todayJobs.filter((t) => !t.assignedTo).sort(byDispatchOrder),
    [todayJobs]
  );

  const techGroups = useMemo(() => {
    // For technicians, only show their own column
    const relevantUids = isTechnician && currentUserUid
      ? Object.keys(techMap).filter((uid) => uid === currentUserUid)
      : Object.keys(techMap);

    return relevantUids.map((uid) => ({
      uid,
      name: techMap[uid],
      jobs: todayJobs
        .filter((t) => t.assignedTo === uid)
        .sort(byDispatchOrder),
    }));
  }, [techMap, todayJobs, isTechnician, currentUserUid]);

  // ── Visible jobs (role-scoped) — used for stats so metrics match the board ──
  // Admins see all jobs; technicians only see their own.
  const visibleJobs = useMemo(() => {
    if (isTechnician && currentUserUid) {
      return todayJobs.filter((t) => t.assignedTo === currentUserUid);
    }
    return todayJobs;
  }, [todayJobs, isTechnician, currentUserUid]);

  // ── Stats (scoped to visibleJobs so numbers match what the user sees) ────────
  const stats = useMemo(() => {
    const total     = visibleJobs.length;
    const completed = visibleJobs.filter((t) => t.status === 'completed').length;
    const inFlight  = visibleJobs.filter((t) => t.status === 'en_route' || t.status === 'on_site').length;
    const remaining = total - completed;
    return { total, completed, inFlight, remaining };
  }, [visibleJobs]);

  // ── Resettable count — drives the "Reset Day" enable state ────────────────
  // Counts jobs that the server would actually unassign: assigned, route-mode,
  // not terminal, not in-flight, lead not closed. This matches the server-side
  // _classify_tasks logic and avoids loading a preview just to find nothing.
  const resettableCount = useMemo(() => {
    const TERMINAL = new Set(['completed', 'cancelled', 'no_show', 'no-show']);
    const IN_FLIGHT = new Set(['en_route', 'on_site']);
    return todayJobs.filter((t) => {
      if (!t.assignedTo) return false;
      if ((t.jobMode || 'route') === 'appointment') return false;
      const s = (t.status || '').toLowerCase();
      if (TERMINAL.has(s) || IN_FLIGHT.has(s)) return false;
      if (isClosedAccount(leadsMap[t.leadId])) return false;
      return true;
    }).length;
  }, [todayJobs, leadsMap]);

  // ── Drag active task ─────────────────────────────────────────────────────────
  const activeTask = useMemo(
    () => activeJobId ? todayJobs.find((t) => t.id === activeJobId) : null,
    [activeJobId, todayJobs]
  );

  // ── Find which container a task belongs to ───────────────────────────────────
  const findContainer = useCallback(
    (taskId) => {
      const task = todayJobs.find((t) => t.id === taskId);
      if (!task) return null;
      return task.assignedTo || 'unassigned';
    },
    [todayJobs]
  );

  // ── Resolve drop target container ────────────────────────────────────────────
  const resolveTargetContainer = useCallback(
    (overId) => {
      if (!overId) return null;
      // Column drop zone IDs
      if (overId === 'drop-unassigned') return 'unassigned';
      if (overId.startsWith('drop-tech-')) return overId.replace('drop-tech-', '');
      // Dropped on a task card → find its container
      const targetTask = todayJobs.find((t) => t.id === overId);
      if (targetTask) return targetTask.assignedTo || 'unassigned';
      return null;
    },
    [todayJobs]
  );

  // ── DnD handlers ─────────────────────────────────────────────────────────────
  const handleDragStart = ({ active }) => {
    setActiveJobId(active.id);
  };

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      setActiveJobId(null);
      if (!over || !active || active.id === over.id) return;

      const draggedTask = todayJobs.find((t) => t.id === active.id);
      if (!draggedTask) return;

      // Appointment jobs cannot be dragged
      if ((draggedTask.jobMode || 'route') === 'appointment') return;

      const fromContainer = draggedTask.assignedTo || 'unassigned';
      const toContainer   = resolveTargetContainer(over.id);
      if (!toContainer) return;

      if (fromContainer !== toContainer) {
        // ── Cross-column move: reassign ──────────────────────────────────────
        const newTechId   = toContainer === 'unassigned' ? null : toContainer;
        const newTechName = newTechId ? techMap[newTechId] : null;

        // Compute drop position in the destination column
        const destColumn = toContainer === 'unassigned'
          ? unassignedJobs
          : (techGroups.find((g) => g.uid === toContainer)?.jobs || []);

        const dropOnTaskIdx = destColumn.findIndex((t) => t.id === over.id);
        const newOrder = dropOnTaskIdx >= 0
          ? (dropOnTaskIdx + 1) * 100
          : (destColumn.length + 1) * 100;

        updateTask(active.id, {
          assignedTo:     newTechId,
          assignedToName: newTechName,
          assignedAt:     newTechId ? new Date().toISOString() : null,
          dispatchOrder:  newOrder,
        });
      } else {
        // ── Same-column reorder ──────────────────────────────────────────────
        const column = fromContainer === 'unassigned'
          ? unassignedJobs
          : (techGroups.find((g) => g.uid === fromContainer)?.jobs || []);

        const oldIdx = column.findIndex((t) => t.id === active.id);
        const newIdx = column.findIndex((t) => t.id === over.id);

        if (oldIdx !== -1 && newIdx !== -1 && oldIdx !== newIdx) {
          // Cannot drop a route job onto an appointment job's slot
          if ((column[newIdx]?.jobMode || 'route') === 'appointment') return;

          const reordered = arrayMove([...column], oldIdx, newIdx);
          reordered.forEach((t, idx) => {
            updateTask(t.id, { dispatchOrder: (idx + 1) * 100 });
          });
        }
      }
    },
    [todayJobs, techMap, techGroups, unassignedJobs, resolveTargetContainer, updateTask]
  );

  // ── Date navigation ──────────────────────────────────────────────────────────
  const goToday  = () => setDispatchDate(new Date());
  const goPrev   = () => setDispatchDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1));
  const goNext   = () => setDispatchDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1));

  // ── Per-tech move job up/down — persists dispatchOrder immediately ───────────
  const handleMoveJob = useCallback((techId, jobId, direction) => {
    const group = techGroups.find((g) => g.uid === techId);
    if (!group) return;
    const col     = group.jobs;
    const fromIdx = col.findIndex((j) => j.id === jobId);
    const toIdx   = fromIdx + (direction === 'up' ? -1 : 1);
    if (fromIdx === -1 || toIdx < 0 || toIdx >= col.length) return;

    // Guard: cannot swap a route job with a fixed appointment job
    if ((col[toIdx].jobMode || 'route') === 'appointment') return;

    const reordered = [...col];
    [reordered[fromIdx], reordered[toIdx]] = [reordered[toIdx], reordered[fromIdx]];

    reordered.forEach((t, idx) => updateTask(t.id, { dispatchOrder: (idx + 1) * 100 }));
    getAuth().currentUser?.getIdToken().then((token) => {
      axios.put(
        `${API}/api/jobs/reorder`,
        { companyId, taskIds: reordered.map((t) => t.id) },
        { headers: { Authorization: `Bearer ${token}` } },
      ).catch((err) => console.error('[Dispatch] move-job error:', err));
    });
  }, [techGroups, updateTask, companyId]);

  // ── Per-tech Optimize Route — route jobs only, appointment jobs stay in place ──
  const handleOptimizeColumnRoute = useCallback(async (techId) => {
    const group = techGroups.find((g) => g.uid === techId);
    if (!group) return null;

    const routeJobs = group.jobs.filter((j) => (j.jobMode || 'route') === 'route');
    const apptCount = group.jobs.length - routeJobs.length;

    if (routeJobs.length < 2) {
      toast.info(apptCount > 0
        ? `${group.name} has no flexible stops to optimize`
        : `${group.name} needs at least 2 jobs to optimize`
      );
      return null;
    }

    const token   = await getAuth().currentUser?.getIdToken();
    const taskIds = routeJobs.map((j) => j.id);

    const res = await axios.post(
      `${API}/api/jobs/optimize-route`,
      { companyId, taskIds },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const { optimizedTaskIds, hasImprovement } = res.data;

    await axios.put(
      `${API}/api/jobs/reorder`,
      { companyId, taskIds: optimizedTaskIds },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    // Preserve column slots: route jobs inherit the dispatchOrder values they held
    // before optimization → appointment jobs remain exactly where they are visually.
    const slotValues = routeJobs.map((j, i) =>
      typeof j.dispatchOrder === 'number' ? j.dispatchOrder : (i + 1) * 100
    );
    optimizedTaskIds.forEach((id, idx) => updateTask(id, { dispatchOrder: slotValues[idx] }));

    const apptMsg = apptCount > 0
      ? ` ${apptCount} fixed appointment${apptCount !== 1 ? 's' : ''} stayed in place.`
      : '';

    if (hasImprovement) {
      toast.success(
        `Optimized ${routeJobs.length} flexible stop${routeJobs.length !== 1 ? 's' : ''}.${apptMsg}`
      );
    } else {
      toast.info(
        apptCount > 0
          ? `Route stops already efficient.${apptMsg}`
          : `${group.name}'s route is already efficient`
      );
    }
    return res.data;
  }, [techGroups, updateTask, companyId]);

  // Is the selected date before today (midnight)? — guards accidental past-date sends
  const isPastDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dispatchDate);
    d.setHours(0, 0, 0, 0);
    return d < today;
  }, [dispatchDate]);

  // ── Sprint 2 — bulk reschedule helpers ───────────────────────────────
  // Jobs eligible for a bulk move: have a scheduledStart on this date and
  // are not yet completed. We exclude completed jobs because moving them
  // would distort historical reporting; operators can re-open if needed.
  const movableTodayJobs = useMemo(
    () => todayJobs.filter((t) => !!t.scheduledStart && t.status !== 'completed'),
    [todayJobs],
  );

  // Drop ids from the selection set whenever the underlying job list shifts
  // (date change, status flip, etc.) so phantom selections can't survive.
  useEffect(() => {
    const validIds = new Set(movableTodayJobs.map((t) => t.id));
    setSelectedJobIds((prev) => {
      let mutated = false;
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
        else mutated = true;
      });
      return mutated ? next : prev;
    });
  }, [movableTodayJobs]);

  const handleToggleSelect = useCallback((taskId) => {
    setSelectedJobIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedJobIds(new Set());
  }, []);

  const selectedTasks = useMemo(
    () => movableTodayJobs.filter((t) => selectedJobIds.has(t.id)),
    [movableTodayJobs, selectedJobIds],
  );

  const bulkModalTasks = useMemo(() => {
    if (bulkRescheduleMode === 'selected') return selectedTasks;
    if (bulkRescheduleMode === 'day')      return movableTodayJobs;
    return [];
  }, [bulkRescheduleMode, selectedTasks, movableTodayJobs]);

  const bulkModalLabel = bulkRescheduleMode === 'selected'
    ? 'Move Selected Jobs'
    : 'Move Day';

  const handleBulkMoved = useCallback(() => {
    setSelectedJobIds(new Set());
    setBulkRescheduleMode(null);
  }, []);

  // Step 1: fetch reminder preview + open modal
  const handleOpenSendReminders = useCallback(async () => {
    if (!companyId || previewLoading) return;
    setPreviewData(null);
    setShowPreview(true);
    setPreviewLoading(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.get(
        `${API}/api/notifications/tomorrow-preview`,
        {
          params: { companyId, targetDate: dispatchDate.toISOString() },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setPreviewData(res.data);
    } catch (err) {
      console.error('[Dispatch] reminders load error:', err);
      toast.error('Could not load reminders preview');
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  }, [companyId, previewLoading, dispatchDate]);

  // Step 2: operator confirms → send reminders for the selected date's jobs
  const handleConfirmSend = useCallback(async () => {
    if (!companyId || sendingReminders) return;
    setSendingReminders(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.post(
        `${API}/api/notifications/visit-reminders/day-before`,
        { companyId, targetDate: dispatchDate.toISOString() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowPreview(false);
      // Durable async batch — open the status panel; sending continues server-side
      setReminderBatchId(res.data.batchId);
      if (res.data.alreadyExisted) {
        toast.info('Reminder batch already exists for this date — showing its status');
      }
    } catch (err) {
      console.error('[Dispatch] send reminders error:', err);
      toast.error('Failed to queue reminders');
    } finally {
      setSendingReminders(false);
    }
  }, [companyId, sendingReminders, dispatchDate]);

  // ── Mobile breakpoint listener ───────────────────────────────────────────────
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────────

  // On mobile: show read-only summary instead of the DnD board
  if (isMobile) return <DispatchMobileSummary />;

  return (
    <div className="flex flex-col h-full" data-testid="dispatch-page">
      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-start justify-between">
          {/* Title + clickable date picker */}
          <div>
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-[var(--cb-orange)]" />
              <h1 className="text-2xl font-bold text-gray-900">Dispatch</h1>
            </div>
            <DateNavigator
              value={dispatchDate}
              onChange={setDispatchDate}
              label={formatDateHeader(dispatchDate)}
              testId="dispatch-date-picker-trigger"
              variant="subtle"
              align="start"
            />
          </div>

          {/* Date controls */}
          <div className="flex items-center gap-2">
            {/* List | Map view toggle — segmented control, admin-only on
                mobile-collapsed pages it falls back to list automatically. */}
            {!isTechnician && (
              <div
                className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 p-0.5"
                role="tablist"
                aria-label="Dispatch view"
                data-testid="dispatch-view-toggle"
              >
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('list');
                    try { localStorage.setItem('dispatch.viewMode', 'list'); } catch (_) { /* ignore quota */ }
                  }}
                  role="tab"
                  aria-selected={viewMode === 'list'}
                  className={`h-7 px-2.5 rounded text-[11px] font-semibold flex items-center gap-1.5 transition-all ${
                    viewMode === 'list'
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="dispatch-view-list"
                >
                  <List className="h-3.5 w-3.5" />
                  List
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('map');
                    try { localStorage.setItem('dispatch.viewMode', 'map'); } catch (_) { /* ignore quota */ }
                  }}
                  role="tab"
                  aria-selected={viewMode === 'map'}
                  className={`h-7 px-2.5 rounded text-[11px] font-semibold flex items-center gap-1.5 transition-all ${
                    viewMode === 'map'
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="dispatch-view-map"
                >
                  <Map className="h-3.5 w-3.5" />
                  Map
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={goPrev}
              className="h-8 w-8 flex items-center justify-center border border-gray-200 rounded-md text-gray-600 bg-white hover:bg-gray-50 transition-colors"
              data-testid="dispatch-prev-btn"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={goToday}
              className="h-8 px-3 text-xs font-medium border border-gray-200 rounded-md text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors"
              data-testid="dispatch-today-btn"
            >
              Today
            </button>
            <button
              type="button"
              onClick={goNext}
              className="h-8 w-8 flex items-center justify-center border border-gray-200 rounded-md text-gray-600 bg-white hover:bg-gray-50 transition-colors"
              data-testid="dispatch-next-btn"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            {!isTechnician && (
              <button
                type="button"
                onClick={() => {
                  setShowClosed((v) => {
                    const next = !v;
                    try { localStorage.setItem('dispatch.showClosed', next ? '1' : '0'); } catch (_) { /* ignore quota */ }
                    return next;
                  });
                }}
                className={`h-8 px-3 text-xs font-medium border rounded-md transition-colors flex items-center gap-1.5 ${
                  showClosed
                    ? 'border-gray-300 bg-gray-100 text-gray-800'
                    : hiddenClosedJobCount > 0
                      ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="dispatch-show-closed-toggle"
                title={hiddenClosedJobCount > 0
                  ? `${hiddenClosedJobCount} job${hiddenClosedJobCount === 1 ? '' : 's'} on closed accounts ${showClosed ? 'visible' : 'hidden'}`
                  : 'Toggle visibility of jobs on closed accounts'}
              >
                {showClosed ? 'Hide closed' : 'Show closed'}
                {!showClosed && hiddenClosedJobCount > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[10px] font-bold px-1"
                    data-testid="dispatch-hidden-closed-count"
                  >
                    {hiddenClosedJobCount}
                  </span>
                )}
              </button>
            )}
            {!isTechnician && (
              <button
                type="button"
                onClick={() => setBuildDayOpen(true)}
                disabled={isPastDate || unassignedJobs.length === 0}
                className={`h-8 px-3 text-xs font-medium border rounded-md transition-colors flex items-center gap-1.5 ${
                  isPastDate || unassignedJobs.length === 0
                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'border-indigo-300 bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
                data-testid="build-day-btn"
                title={
                  isPastDate
                    ? 'Past date — cannot plan'
                    : unassignedJobs.length === 0
                      ? 'No unassigned jobs to plan'
                      : 'Recommend technician assignments for the unassigned column'
                }
              >
                <Wand2 className="h-3.5 w-3.5" />
                Build Day
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setResetSheet({ scope: 'day' })}
                disabled={resettableCount === 0}
                className={`h-8 px-3 text-xs font-medium border rounded-md transition-colors flex items-center gap-1.5 ${
                  resettableCount === 0
                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                }`}
                data-testid="reset-day-btn"
                title={
                  resettableCount === 0
                    ? 'No assigned route jobs to reset on this day'
                    : 'Return route-mode jobs to Unassigned (locked appointments and in-flight stops stay)'
                }
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset Day
              </button>
            )}
            {isAdminView && (
              <button
                type="button"
                onClick={() => setBulkRescheduleMode('day')}
                disabled={isPastDate || movableTodayJobs.length === 0}
                className={`h-8 px-3 text-xs font-medium border rounded-md transition-colors flex items-center gap-1.5 ${
                  isPastDate || movableTodayJobs.length === 0
                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100'
                }`}
                data-testid="move-day-btn"
                title={
                  isPastDate
                    ? 'Past date — cannot bulk-move historical jobs'
                    : movableTodayJobs.length === 0
                      ? 'No movable jobs on this day'
                      : `Move all ${movableTodayJobs.length} jobs to another day (default reason: weather)`
                }
              >
                <CloudSnow className="h-3.5 w-3.5" />
                Move Day…
              </button>
            )}
            {!isTechnician && (
              <button
                type="button"
                onClick={handleOpenSendReminders}
                disabled={previewLoading || isPastDate}
                className={`h-8 px-3 text-xs font-medium border rounded-md transition-colors flex items-center gap-1.5 ${
                  isPastDate
                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : previewLoading
                      ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                }`}
                data-testid="send-reminders-btn"
                title={isPastDate
                  ? 'Past date — reminders would arrive after the visit'
                  : `Send reminders for jobs on this date`}
              >
                <Bell className={`h-3.5 w-3.5 ${previewLoading ? 'animate-pulse' : ''}`} />
                {previewLoading ? 'Loading…' : 'Send Reminders'}
              </button>
            )}
          </div>
        </div>

        {/* Stats strip */}
        {stats.total > 0 && (
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <StatChip
              icon={<CalendarDays className="h-3.5 w-3.5" />}
              label="Total"
              value={stats.total}
              color="text-gray-600"
            />
            <StatChip
              icon={<CheckCircle2 className="h-3.5 w-3.5" />}
              label="Complete"
              value={stats.completed}
              color="text-emerald-600"
            />
            <StatChip
              icon={<Clock className="h-3.5 w-3.5" />}
              label="Remaining"
              value={stats.remaining}
              color="text-amber-600"
            />
            <StatChip
              icon={<Truck className="h-3.5 w-3.5" />}
              label="In Progress"
              value={stats.inFlight}
              color="text-indigo-600"
            />
            <StatChip
              icon={<Users className="h-3.5 w-3.5" />}
              label="Technicians"
              value={techGroups.length}
              color="text-gray-600"
            />
          </div>
        )}
      </div>

      {/* ── Sprint 2: floating selection action bar ───────────────────── */}
      {selectedJobIds.size > 0 && isAdminView && (
        <div
          className="sticky top-0 z-20 flex items-center justify-between gap-3 px-4 py-2 border-b border-emerald-200 bg-emerald-50 shadow-sm"
          data-testid="dispatch-selection-bar"
        >
          <div className="flex items-center gap-2 text-sm">
            <span
              className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-emerald-600 text-white text-[11px] font-bold"
              data-testid="dispatch-selection-count"
            >
              {selectedJobIds.size}
            </span>
            <span className="text-emerald-900 font-medium">
              job{selectedJobIds.size === 1 ? '' : 's'} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkRescheduleMode('selected')}
              className="h-8 px-3 text-xs font-semibold border border-sky-300 bg-sky-600 text-white hover:bg-sky-700 rounded-md transition-colors flex items-center gap-1.5"
              data-testid="move-selected-btn"
            >
              <CloudSnow className="h-3.5 w-3.5" />
              Move Selected Jobs…
            </button>
            <button
              type="button"
              onClick={handleClearSelection}
              className="h-8 px-2.5 text-xs font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 rounded-md transition-colors flex items-center gap-1"
              data-testid="dispatch-clear-selection-btn"
            >
              <XIcon className="h-3.5 w-3.5" />
              Clear
            </button>
          </div>
        </div>
      )}

      {/* ── Board OR Map View ─────────────────────────────────────────── */}
      {viewMode === 'map' && !isTechnician ? (
        <MapView companyId={companyId} dispatchDate={dispatchDate} />
      ) : (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* Synced top scrollbar — desktop only, hidden when board doesn't overflow */}
        <DispatchScrollSync contentRef={boardScrollRef} />

        <div ref={boardScrollRef} className="flex-1 overflow-x-auto overflow-y-hidden" data-testid="dispatch-board-scroll">
          <div className="flex gap-3 p-4 h-full items-start min-w-max">
            {/* Unassigned rail — always visible to admins */}
            {isAdminView && (
              <DispatchUnassignedRail
                jobs={unassignedJobs}
                canDrag={canDrag}
                isAdminView={isAdminView}
                leadsMap={leadsMap}
                selectedJobIds={selectedJobIds}
                onToggleSelect={handleToggleSelect}
              />
            )}

            {/* Technician columns */}
            {techGroups.map((group) => (
              <DispatchTechColumn
                key={group.uid}
                techId={group.uid}
                techName={group.name}
                jobs={group.jobs}
                canDrag={canDrag}
                isAdminView={isAdminView}
                currentUserUid={currentUserUid}
                leadsMap={leadsMap}
                onOptimize={() => handleOptimizeColumnRoute(group.uid)}
                onMoveJob={(jobId, dir) => handleMoveJob(group.uid, jobId, dir)}
                onReset={
                  isAdmin
                    ? () => setResetSheet({ scope: 'technician', techUid: group.uid, techName: group.name })
                    : null
                }
                selectedJobIds={selectedJobIds}
                onToggleSelect={handleToggleSelect}
              />
            ))}

            {/* Empty state */}
            {todayJobs.length === 0 && (
              <EmptyState date={dispatchDate} />
            )}

            {/* No techs assigned yet but there are jobs */}
            {todayJobs.length > 0 && techGroups.length === 0 && isAdminView && (
              <div className="flex flex-col items-center justify-center w-64 min-h-[200px] rounded-xl border-2 border-dashed border-gray-200 text-center p-6">
                <Users className="h-8 w-8 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-500">No technicians assigned</p>
                <p className="text-xs text-gray-400 mt-1">
                  Drag jobs from the unassigned rail to a technician column once you&apos;ve assigned them via the Scheduler.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Drag overlay — floating card while dragging */}
        <DragOverlay>
          {activeTask ? (
            <DispatchJobCard
              task={activeTask}
              lead={leadsMap[activeTask.leadId]}
              canDrag={false}
              canChangeStatus={false}
              isDragOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      )}
      <SendRemindersModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        preview={previewData}
        loading={previewLoading}
        onConfirmSend={handleConfirmSend}
        sending={sendingReminders}
        dispatchDate={dispatchDate}
      />
      <BuildDaySheet
        open={buildDayOpen}
        onOpenChange={setBuildDayOpen}
        dispatchDate={dispatchDate}
        companyId={companyId}
        onSendReminders={() => {
          setBuildDayOpen(false);
          handleOpenSendReminders();
        }}
      />
      <ResetSheet
        open={!!resetSheet}
        onOpenChange={(o) => { if (!o) setResetSheet(null); }}
        companyId={companyId}
        dispatchDate={dispatchDate}
        scope={resetSheet?.scope}
        techUid={resetSheet?.techUid}
        techName={resetSheet?.techName}
      />
      <BulkRescheduleModal
        isOpen={!!bulkRescheduleMode && bulkModalTasks.length > 0}
        onClose={() => setBulkRescheduleMode(null)}
        fromDate={dispatchDate}
        tasks={bulkModalTasks}
        leadsById={leadsMap}
        label={bulkModalLabel}
        onMoved={handleBulkMoved}
      />
      <ReminderBatchPanel
        open={!!reminderBatchId}
        onClose={() => setReminderBatchId(null)}
        batchId={reminderBatchId}
        dateLabel={dispatchDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
      />
    </div>
  );
}

// ── Stat chip ──────────────────────────────────────────────────────────────────

function StatChip({ icon, label, value, color }) {
  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium ${color}`}>
      {icon}
      <span>{value} {label}</span>
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────────

function EmptyState({ date }) {
  const today = new Date();
  const isToday =
    date.getDate()     === today.getDate() &&
    date.getMonth()    === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  return (
    <div className="flex flex-col items-center justify-center w-full min-h-[300px] rounded-xl border-2 border-dashed border-gray-200 text-center p-8">
      <Truck className="h-10 w-10 text-gray-300 mb-4" />
      <p className="text-base font-semibold text-gray-500">
        No jobs scheduled {isToday ? 'today' : 'on this day'}
      </p>
      <p className="text-sm text-gray-400 mt-1 max-w-xs">
        {isToday
          ? 'Schedule jobs on the Calendar and assign them to technicians to see them here.'
          : 'Use the date controls to navigate to a day with scheduled jobs.'}
      </p>
    </div>
  );
}
